import { cn } from "../../utils/cn";
import { Button } from "../button/button";
import { TextD2C } from "../text-d2c/text";

export type ProductVariantSelectorStockStatus =
  | "inStock"
  | "availableInFuture"
  | "outOfStock"
  | "unknown";

export interface ProductVariantSelectorOption {
  id: string;
  ean: string;
  variantCode: string;
  label: string;
  href?: string;
  selected?: boolean;
  disabled?: boolean;
  stockStatus?: ProductVariantSelectorStockStatus;
  stockMessage?: string;
}

type ProductVariantSelectorColumns = 2 | 3 | 4 | 5;

export interface ProductVariantSelectorProps {
  label: string;
  value?: string;
  options: ProductVariantSelectorOption[];
  className?: string;
  columns?: ProductVariantSelectorColumns;
  statusBadgeLabels?: Partial<
    Record<ProductVariantSelectorStockStatus, string>
  >;
}

const gridColumnClassNames: Record<ProductVariantSelectorColumns, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
};

const statusBadgeBgClass: Partial<
  Record<ProductVariantSelectorStockStatus, string>
> = {
  inStock: "bg-alabastergrey",
  availableInFuture: "bg-alabastergrey",
  outOfStock: "bg-alabastergrey",
};

const statusDotClass: Partial<
  Record<ProductVariantSelectorStockStatus, string>
> = {
  inStock: "bg-neonchartreuse",
  availableInFuture: "bg-warning-medium",
  outOfStock: "bg-danger-medium",
};

function getSelectedBadge(
  options: ProductVariantSelectorOption[],
  statusBadgeLabels: ProductVariantSelectorProps["statusBadgeLabels"]
): { label: string; status: ProductVariantSelectorStockStatus } | undefined {
  const selectedOption = options.find((option) => option.selected);

  if (!selectedOption?.stockStatus) {
    return;
  }

  const label =
    selectedOption.stockMessage ??
    statusBadgeLabels?.[selectedOption.stockStatus];

  if (!label) {
    return;
  }

  return { label, status: selectedOption.stockStatus };
}

export function ProductVariantSelector(
  props: Readonly<ProductVariantSelectorProps>
) {
  if (props.options.length === 0) {
    return null;
  }

  const selectedBadge = getSelectedBadge(
    props.options,
    props.statusBadgeLabels
  );

  return (
    <div
      className={cn("space-y-3", props.className)}
      id="product-variant-selector"
    >
      <TextD2C style="bold" variant="p-small">
        {props.label}
        {props.value ? `: ${props.value}` : ""}
      </TextD2C>

      {selectedBadge && (
        <span
          className={cn(
            "mb-3 inline-flex rounded-full px-3 py-1.5",
            statusBadgeBgClass[selectedBadge.status] ?? "bg-alabastergrey"
          )}
        >
          <TextD2C variant="pillar">{selectedBadge.label}</TextD2C>
        </span>
      )}

      <div
        className={cn(
          "product_detail__description__size--sizes",
          props.columns
            ? `grid gap-3 ${gridColumnClassNames[props.columns]}`
            : "flex flex-wrap gap-2"
        )}
      >
        {props.options.map((option) => {
          const dotClass =
            option.stockStatus && statusDotClass[option.stockStatus];

          return (
            <Button
              aria-current={option.selected ? "true" : undefined}
              className={cn(
                "relative h-9 border-alabastergrey py-2 font-bold text-black text-xs leading-5",
                option.selected &&
                  "bg-alabastergrey hover:bg-alabastergrey active:text-black"
              )}
              data-product-ean={option.ean}
              data-test-id={`product-variant-selector-option-${option.id}`}
              data-variantcode={option.variantCode}
              disabled={option.disabled}
              href={option.href && !option.disabled ? option.href : undefined}
              key={option.id}
              mode="D2C"
              type={option.href ? undefined : "button"}
              variant={option.selected ? "default" : "outline"}
            >
              {option.label}
              {dotClass && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute top-1 right-1 size-2 rounded-full",
                    dotClass
                  )}
                />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
